import * as modal from "../util/modal.ts";
import * as Html from "../util/Html.ts";
import ApiServer from "../ApiServer.ts";
import {SyncCache} from "../lib/SyncCache.ts";
import * as DateUtils from "../lib/DateUtils.ts";
import {EventStore} from "../server/EventStore.ts";
import * as amplify from "../util/amplify.ts";
import {register} from "../widget/action-menu-registry.ts";
import Icons from "../svg/Icons.ts";
import * as i18n from "../i18n.ts";
import TPL_LIST from "./manageimport_list.mustache";
import TPL_DETAIL_EVENTS from "./manageimport_detail_events.mustache";
import { isFeatureEnabled } from "../lib/launchdarkly/launchdarkly.ts";
import type { ExternalImportBean } from "../server/fizapilib.auto.ts";
import * as FwDatas from "../FwDatas.ts";

export const eventImportCache = new SyncCache<ApiServer.types.ExternalImportBean>(
  ApiServer.metasync.prepare("externalImport"),
  function(a,b){ return (b.creationDate?.getTime()||0) - (a.creationDate?.getTime()||0) }
);

let currentFilter = 'all';
let eventCountByImportId: Map<string, number> = new Map();
let cachedEvents: ApiServer.types.IEvent[] = [];

async function buildEventCounts(){
  cachedEvents = await EventStore.getFor(null);
  var counts = new Map<string, number>();
  cachedEvents.forEach(ev=>{
    if(ev.externalImportId){
      counts.set(ev.externalImportId, (counts.get(ev.externalImportId) || 0) + 1);
    }
  });
  eventCountByImportId = counts;
}

const importTypeEmoji = {
  'EVENT_BY_INBOX': '📧',
  'EVENT_BY_URL': '🔗',
  'EVENT_BY_ICAL': '📎',
  'EVENT_BY_AI_UPLOAD': '✨'
};

const AI_FLAG = 'fwall-32-ai-event-import-quick-add';
const HISTORY_LIMIT = 20;
const LAST_DAYS = 7*24*3600*1000;
//the icon of the row, and the tone of the square around it
const sourceIcon = {
  EVENT_BY_INBOX:{icon:Icons.ic_email, tone:'email'},
  EVENT_BY_URL:{icon:Icons.ic_link, tone:'url'},
  EVENT_BY_ICAL:{icon:Icons.ic_file, tone:'ics'},
  photo:{icon:Icons.ic_gallery, tone:'ai'},
  file:{icon:Icons.ic_file, tone:'ai'},
  voice:{icon:Icons.ic_voice, tone:'ai'}
};

/**
 * The server does not tell the modality of an AI import: its info is the file name, or the text itself when it was
 * dictated
 */
function aiModality(item :ExternalImportBean) :'photo'|'file'|'voice'{
  const info = item.info||'';
  if(/\.(jpe?g|png|heic|heif|webp)$/i.test(info)) return 'photo';
  if(/\.\w{2,4}$/.test(info)) return 'file';
  return 'voice';
}

function isAiPending(item :ExternalImportBean){
  return item.importType==='EVENT_BY_AI_UPLOAD' && (item.runState==='ONGOING' || item.runState==='ERROR');
}

function matchesFilter(item :ExternalImportBean){
  if(currentFilter==='all') return true;
  if(currentFilter.startsWith('ai-')) return item.importType==='EVENT_BY_AI_UPLOAD' && 'ai-'+aiModality(item)===currentFilter;
  return item.importType===currentFilter;
}

/**
 * The history of FWWEB-1850: the AI imports parsing or failed are shown too, grouped in Ongoing, Last 7 Days and
 * Earlier, the empty groups not shown
 */
function groupImports(imports: ExternalImportBean[]){
  const
    now = Date.now(),
    rows = imports
      .filter(item=>isAiPending(item) || eventCountByImportId.get(item.metaId)>0)
      .filter(matchesFilter)
      .slice(0, HISTORY_LIMIT)
      .map(item=>{
        const
          ai = item.importType==='EVENT_BY_AI_UPLOAD',
          failed = ai && item.runState==='ERROR',
          source = sourceIcon[ai?aiModality(item):item.importType] || sourceIcon.EVENT_BY_ICAL;
        return {
          ...formatImport(item),
          //an AI import is told by who made it, the classic ones by their file, address or sender
          info: ai?i18n.get('manageimport.from',{name:FwDatas.getProfile(item.accountId).name}):(item.info || ''),
          date: item.creationDate?item.creationDate.toLocaleString(i18n.getSelectedLanguage(),{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}):'',
          icon: failed?Icons.ic_error:source.icon,
          tone: failed?'failed':source.tone,
          ongoing: ai && item.runState==='ONGOING',
          failed,
          time: item.creationDate?.getTime()||0
        }
      }),
    groups = [
      {title:'manageimport.group.ongoing', items:rows.filter(row=>row.ongoing)},
      {title:'manageimport.group.last7days', items:rows.filter(row=>!row.ongoing && now-row.time<LAST_DAYS)},
      {title:'manageimport.group.earlier', items:rows.filter(row=>!row.ongoing && now-row.time>=LAST_DAYS)}
    ].filter(group=>group.items.length>0);
  return {groups, items:rows.length>0, grouped:true};
}

function formatImport(item :ExternalImportBean){
  return {
    metaId: item.metaId,
    importType: item.importType,
    emoji: importTypeEmoji[item.importType] || '📅',
    eventCount: eventCountByImportId.get(item.metaId) || 0,
    oneEvent: eventCountByImportId.get(item.metaId)===1,
    info: item.info || '',
    date: item.creationDate ? DateUtils.format(item.creationDate) : ''
  }
}

function listData(imports: ExternalImportBean[]){
  return isFeatureEnabled(AI_FLAG)?groupImports(imports):{items:formatImports(imports)};
}

function formatImports(imports: ApiServer.types.ExternalImportBean[]){
  imports = imports.filter(item=>eventCountByImportId.get(item.metaId)>0)
  const filtered = currentFilter === 'all' ? imports : imports.filter(i=>i.importType === currentFilter);
  return filtered.map(formatImport)
}

function refreshList(){
  var list = document.querySelector('.manageimport-list') as HTMLElement;
  if(list) Html.setHtml(list, TPL_LIST, listData(eventImportCache.datas || []));
}


Html.registerDom({
  async manageimport(){
    currentFilter = 'all';
    var [imports] = await Promise.all([eventImportCache.need(), buildEventCounts()]);
    var domModal = modal.openDomModal(await import('./manageimport.mustache'),{
      ...listData(imports),
      aiFilters: isFeatureEnabled(AI_FLAG)
    },{
      async eventImportUpdated(){
        await eventImportCache.need();
        await buildEventCounts();
        refreshList();
      }
    }), pane = domModal.querySelector('.pane-right')
    setTimeout(()=>pane?.classList.add('open'),33)
  },
  async openImportDetail(importId: string){
    var item = eventImportCache.get(importId);
    if(!item) return;
    if(item.importType==='EVENT_BY_AI_UPLOAD' && item.runState==='ONGOING'){
      Html.trigger(this,'aiimportstill');
      return;
    }
    if(item.importType==='EVENT_BY_AI_UPLOAD' && item.runState==='ERROR'){
      //nothing was created, the failed import only leaves the history once seen
      await modal.askAsPromise({
        lottie:'images/lottie/dizzy_face.json',
        title:'aiimport.failed.title',
        message:'aiimport.failed.sheet',
        button:[{title:'actions.gotit', value:'true'}]
      });
      await ApiServer.evtexternalimportdelete(false, [importId]);
      eventImportCache.invalidate();
      amplify.publish("eventImportUpdated");
      return;
    }

    var matchingEvents = cachedEvents.filter(ev=>ev.externalImportId === importId);

    var domModal = modal.openDomModal(await import('./manageimport_detail.mustache'),{
      importId,
      info: item.info || '',
      date: item.creationDate ? DateUtils.format(item.creationDate) : '',
      eventCount: matchingEvents.length,
      events: matchingEvents.slice(0,50).map(ev=>({
        metaId: ev.metaId,
        text: ev.text || '',
        date: ev.startDate ? DateUtils.format(ev.startDate) : '',
        color: ev.colorUI
      }))
    },{
      async eventDeleted(){
        EventStore.invalidate();
        await refreshDetail();
        amplify.publish("eventImportUpdated");
      },
      async eventImportUpdated(){
        await refreshDetail();
      }
    }), pane = domModal.querySelector('.pane-right')

    async function refreshDetail(){
      await buildEventCounts();
      var remaining = cachedEvents.filter(ev=>ev.externalImportId === importId);
      if(remaining.length === 0){
        modal.closeDomModal(domModal);
      }else{
        Html.setHtml(
          domModal.querySelector('.manageimport-detail-events') as HTMLElement,
          TPL_DETAIL_EVENTS,
          {events: remaining.slice(0,50).map(ev=>({metaId:ev.metaId, text:ev.text||'', date:ev.startDate ? DateUtils.format(ev.startDate) : '', color:ev.colorUI}))}
        );
        var countEl = domModal.querySelector('.manageimport-detail-count strong');
        if(countEl) countEl.textContent = remaining.length + countEl.textContent.replace(/^\d+/,'');
      }
    }
    setTimeout(()=>pane?.classList.add('open'), 33);
  },
  async manageimportdelete(importId: string){
    if(isFeatureEnabled(AI_FLAG) && eventImportCache.get(importId)?.importType==='EVENT_BY_AI_UPLOAD'){
      Html.trigger(this,'aiimportundo',[importId]);
      return;
    }
    var confirmed = await modal.confirmDelete("manageimport.detail.confirmdelete");
    if(!confirmed) return;
    await ApiServer.evtexternalimportdelete(true, [importId]);
    modal.closeDomModal();
    modal.toastDone('manageimport.detail.toastdeleted');
    eventImportCache.invalidate();
    EventStore.invalidate();
    amplify.publish("eventImportUpdated");
  },
  async manageimportfilter(value){
    currentFilter = value;
    await eventImportCache.need();

    var list = document.querySelector('.manageimport-list') as HTMLElement;
    if(!list) return;

    var pane = list.closest('.pane-right');
    var chips = pane?.querySelectorAll('.rounded-corner-chips');
    chips?.forEach(chip=>{
      var onclick = chip.getAttribute('data-fw-onclick');
      chip.classList.toggle('selected', onclick === 'manageimportfilter|'+value);
    });

    refreshList();
  }
})

register({
  manageimportbatch(){
    return {
      menu:[
        {
          svg: Icons.ic_member_select,
          i18nkey: 'manageimport.batch.assign',
          onclick: 'batchassign'
        },
        {
          svg: Icons.ic_palette,
          i18nkey: 'manageimport.batch.color',
          onclick: 'batchcolor'
        }
      ]
    }
  }
});

Html.registerDom({
  async batchassign(){
    var importId = Html.getDomMetaId(this);
    var result = await modal.pickAssignee(i18n.get('manageimport.batch.assign'));
    if(!result) return;
    var events = cachedEvents.filter(ev=>ev.externalImportId === importId);
    var metaIds = events.map(ev=>ev.metaId);
    var attendee = result.accountIds.map(id=>({accountId:id, status:'ACCEPTED'}));
    var count = await ApiServer.evtbatchupdate(metaIds, new ApiServer.types.EventInputBean({attendee, isToAll:result.isToAll} as any));
    modal.toastDone('manageimport.batch.done', count);
    EventStore.invalidate();
    amplify.publish("eventImportUpdated");
  },
  async batchcolor(){
    var importId = Html.getDomMetaId(this);
    var color = await modal.pickColor(i18n.get('manageimport.batch.color'));
    if(!color) return;
    var events = cachedEvents.filter(ev=>ev.externalImportId === importId);
    var metaIds = events.map(ev=>ev.metaId);
    var count = await ApiServer.evtbatchupdate(metaIds, new ApiServer.types.EventInputBean({color}));
    modal.toastDone('manageimport.batch.done', count);
    EventStore.invalidate();
    amplify.publish("eventImportUpdated");
  }
});

export { eventImportCache as importCache }
